<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import { getMaterialLink } from '~/shared/lib/getMaterialLink';
import { resizeImageAttrs } from '~/shared/lib/resizeImage';

const props = defineProps<{
  id: number;
  expertUrlId?: number | null;
  title: string;
  subject: string | null;
  image?: string | null;
  author: string | null;
  redirect?: string | null;
  textColor: string;
  type?: 'news' | 'analytics' | 'experts' | 'expert' | 'new_object';
  /* Ссылка не на материал, а куда-то ещё (например, на мероприятие) */
  to?: RouteLocationRaw;
}>();

const isRedirect = computed(() =>
  Boolean(props.redirect && props.redirect.length > 0),
);

const link = computed(
  () =>
    props.to ??
    getMaterialLink({
      id: props.id,
      type: props.type,
      expert_url_id: props.expertUrlId,
      redirect: props.redirect,
    }),
);
const imageAttrs = computed(() =>
  resizeImageAttrs(props.image, { width: 80, height: 80, mode: 'fill' }),
);
</script>

<template>
  <NuxtLink
    :to="link"
    :class="$style.item"
    :style="{
      '--text-color': textColor,
    }"
    :target="isRedirect ? '_blank' : undefined"
  >
    <div :class="$style.item__title">
      {{ props.title }}
    </div>
    <div :class="$style.item__group">
      <div :class="$style.item__text" v-html="props.subject" />
      <div v-if="image" :class="$style.item__imageWrapper">
        <img
          :class="$style.item__image"
          :alt="props.title"
          :src="imageAttrs.src"
          :srcset="imageAttrs.srcset"
        />
      </div>
    </div>
    <div v-if="author" :class="$style.item__author">
      {{ author }}
    </div>
  </NuxtLink>
</template>

<style module lang="scss">
.item {
  display: flex;
  height: 100%;
  flex-flow: column nowrap;
  gap: var(--space-1-5x);
  padding: var(--space-2-5x);
  text-decoration: none;

  &__title {
    @include text_h5;
    @include line_clamp(4);
    color: var(--text-color);
  }

  &__group {
    display: flex;
    flex-flow: row nowrap;
    gap: var(--space-2x);
    justify-content: space-between;
  }

  &__text {
    @include text_body2;
    color: var(--text-color);
    @include line_clamp(4);
  }

  &__author {
    @include text_body2;
    color: var(--color-gray-text);
    margin-top: auto;
  }

  &__image {
    width: 80px;
    height: 80px;
    object-fit: cover;
    background-repeat: no-repeat;
    flex-shrink: 0;

    &Wrapper {
      display: flex;
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 479px) {
  .item {
    padding: var(--space-2x);
  }
}
</style>
